import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AdminPage from "../components/admin/AdminPage";
import AdminRoleConfirmDialog from "../components/admin/AdminRoleConfirmDialog";
import AdminSection from "../components/admin/AdminSection";
import { ADMIN_TOOLBAR_SX } from "../components/admin/adminStyles";
import { useAuth } from "../contexts/AuthContext";
import { useAdminUsers, useChangeAdminRole } from "../hooks/useAdmin";
import { QUERY_PAGE_SIZE_OPTIONS } from "../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";
import {
  filterAdminUsers,
  formatAdminDate,
  isSameUser,
  providerLabel,
  toAdminUserRow,
} from "../utils/adminUsers";

const AdminUsers = () => {
  const { user: actor } = useAuth();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [sortModel, setSortModel] = useState([
    { field: "createdAt", sort: "desc" },
  ]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleIntent, setRoleIntent] = useState(null);
  const [status, setStatus] = useState({ error: null, success: null });

  const {
    users,
    totalResults,
    isLoading,
    error,
    refetch,
  } = useAdminUsers({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });
  const { changeAdminRole, isLoading: roleLoading } = useChangeAdminRole();

  const filteredUsers = useMemo(
    () =>
      filterAdminUsers(users, { searchTerm, roleFilter, providerFilter }),
    [users, searchTerm, roleFilter, providerFilter]
  );
  const usersData = filteredUsers.map(toAdminUserRow);
  const sortedField = sortModel[0]?.field;
  const selectedRow = selectedUser ? toAdminUserRow(selectedUser) : null;
  const viewingSelf = selectedUser && isSameUser(actor, selectedUser);

  const openUser = (userId) => {
    const user = users.find((item) => item._id === userId);
    if (user) setSelectedUser(user);
  };

  const handleRoleChange = async (user) => {
    const makeAdmin = roleIntent === true;
    const result = await changeAdminRole(user._id, makeAdmin);
    if (result.success) {
      setStatus({
        error: null,
        success: makeAdmin
          ? "Admin access granted."
          : "Admin access removed.",
      });
      setRoleIntent(null);
      setSelectedUser(null);
      await refetch();
    } else {
      setStatus({ error: result.error, success: null });
    }
  };

  const columns = [
    {
      field: "username",
      headerName: "User",
      flex: 1,
      minWidth: 200,
      headerClassName: sortedClassFor("username", sortedField),
      cellClassName: sortedClassFor("username", sortedField),
      sortingOrder: NAME_SORT_ORDER,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.row.avatar ? (
            <Avatar
              src={params.row.avatar}
              alt={params.value}
              sx={{ width: 32, height: 32 }}
            />
          ) : (
            <Avatar sx={{ width: 32, height: 32 }}>
              {String(params.value || "?").slice(0, 1).toUpperCase()}
            </Avatar>
          )}
          <Box>
            <Box sx={{ fontWeight: 700 }}>{params.value}</Box>
            {params.row.firstName && params.row.lastName && (
              <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                {params.row.firstName} {params.row.lastName}
              </Box>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      headerClassName: sortedClassFor("email", sortedField),
      cellClassName: sortedClassFor("email", sortedField),
      sortingOrder: NAME_SORT_ORDER,
    },
    {
      field: "provider",
      headerName: "Provider",
      width: 120,
      renderCell: (params) => (
        <Chip label={providerLabel(params.value)} size="small" variant="outlined" />
      ),
    },
    {
      field: "role",
      headerName: "Role",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "Admin" ? "warning" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      type: "number",
      headerClassName: sortedClassFor("createdAt", sortedField),
      cellClassName: sortedClassFor("createdAt", sortedField),
      sortingOrder: STAT_SORT_ORDER,
      renderCell: (params) => formatAdminDate(params.value),
    },
    {
      field: "lastLogin",
      headerName: "Last login",
      width: 120,
      type: "number",
      headerClassName: sortedClassFor("lastLogin", sortedField),
      cellClassName: sortedClassFor("lastLogin", sortedField),
      sortingOrder: STAT_SORT_ORDER,
      renderCell: (params) => formatAdminDate(params.value),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            openUser(params.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <AdminPage
      title="Users"
      subtitle="Accounts, roles, and admin access."
      error={status.error || error}
      onDismissError={() => setStatus((prev) => ({ ...prev, error: null }))}
    >
      {status.success && (
        <Typography color="success.main" sx={{ mb: 2 }}>
          {status.success}
        </Typography>
      )}

      <AdminSection
        title={`Directory · ${usersData.length} on this page`}
        action={
          <Typography variant="body2" color="text.secondary">
            {totalResults.toLocaleString()} total
          </Typography>
        }
      >
        <Box sx={{ ...ADMIN_TOOLBAR_SX, mb: 2 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            sx={{ minWidth: 220 }}
            placeholder="Name or email"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={providerFilter}
              label="Provider"
              onChange={(event) => setProviderFilter(event.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="google">Google</MenuItem>
              <MenuItem value="local">Local</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={usersData}
            columns={columns}
            pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={totalResults}
            disableRowSelectionOnClick
            disableColumnMenu
            loading={isLoading}
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            onRowClick={(params) => openUser(params.id)}
            rowHeight={52}
            sx={{ ...getResultsGridSx(), cursor: "pointer" }}
          />
        </Box>
      </AdminSection>

      <Dialog
        open={Boolean(selectedUser) && roleIntent == null}
        onClose={() => setSelectedUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Stack spacing={1.25} sx={{ pt: 1 }}>
              <Typography>
                <strong>Name:</strong> {selectedRow.username}
              </Typography>
              <Typography>
                <strong>Email:</strong> {selectedUser.email || "—"}
              </Typography>
              <Typography>
                <strong>Provider:</strong> {providerLabel(selectedUser.provider)}
              </Typography>
              <Typography>
                <strong>Role:</strong> {selectedRow.role}
              </Typography>
              <Typography>
                <strong>Created:</strong>{" "}
                {formatAdminDate(selectedRow.createdAt)}
              </Typography>
              {viewingSelf && selectedRow.isAdmin && (
                <Typography variant="body2" color="text.secondary">
                  You cannot remove your own admin access.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedUser(null)}>Close</Button>
          {selectedRow && !selectedRow.isAdmin && (
            <Button
              color="warning"
              variant="outlined"
              onClick={() => setRoleIntent(true)}
            >
              Grant admin…
            </Button>
          )}
          {selectedRow?.isAdmin && !viewingSelf && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => setRoleIntent(false)}
            >
              Remove admin…
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminRoleConfirmDialog
        open={Boolean(selectedUser) && roleIntent != null}
        user={selectedUser}
        makeAdmin={roleIntent === true}
        isLoading={roleLoading}
        error={status.error}
        onCancel={() => {
          setRoleIntent(null);
          setStatus((prev) => ({ ...prev, error: null }));
        }}
        onConfirm={handleRoleChange}
      />
    </AdminPage>
  );
};

export default AdminUsers;
